import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './NewsletterSignup.css';

type Status = 'idle' | 'sending' | 'done' | 'error';

interface NewsletterSignupProps {
  /** Recorded against the subscriber so you can see which placements work. */
  source: string;
  /** 'panel' for a full block, 'compact' for the footer. */
  variant?: 'panel' | 'compact';
}

/**
 * Weekly newsletter signup.
 *
 * The consent box is deliberately unticked and separate from the email field:
 * UK GDPR requires consent to be a positive action, and PECR requires it to be
 * specific to marketing rather than bundled into using the site.
 */
export default function NewsletterSignup({ source, variant = 'panel' }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const compact = variant === 'compact';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;

    if (!consent) {
      setStatus('error');
      setMessage('Please tick the box to confirm you would like the newsletter.');
      return;
    }

    setStatus('sending');
    setMessage('');

    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, consent, source, website }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatus('error');
        setMessage(data.error ?? 'Something went wrong. Please try again.');
        return;
      }

      setStatus('done');
      setMessage(
        data.status === 'pending'
          ? 'Almost there — check your inbox and click the link to confirm.'
          : 'You are on the list. The first one arrives on Thursday.',
      );
      setEmail('');
      setConsent(false);
    } catch {
      setStatus('error');
      setMessage('Could not reach the server. Please try again.');
    }
  }

  if (status === 'done') {
    return (
      <div className={`newsletter ${compact ? 'newsletter--compact' : ''} newsletter--done`}>
        <span className="newsletter-done-emoji" aria-hidden="true">🌱</span>
        <p className="text-hand newsletter-done-text">{message}</p>
      </div>
    );
  }

  return (
    <div className={`newsletter ${compact ? 'newsletter--compact' : ''}`}>
      {!compact && (
        <>
          <h2 className="newsletter-title text-headline">Thursday mornings, from the garden</h2>
          <p className="newsletter-blurb text-body">
            A short weekly letter — what to sow, the moon's day type for the week ahead, and
            the weekend's weather for your plot. Free, and you can leave whenever you like.
          </p>
        </>
      )}
      {compact && <h3 className="footer-col-title text-label">Weekly newsletter</h3>}

      <form className="newsletter-form" onSubmit={onSubmit} noValidate>
        <div className="newsletter-row">
          <label className="newsletter-label" htmlFor={`nl-email-${source}`}>
            Email address
          </label>
          <input
            id={`nl-email-${source}`}
            className="newsletter-input input"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>

        {/* Honeypot: hidden from people, irresistible to bots */}
        <div className="newsletter-hp" aria-hidden="true">
          <label htmlFor={`nl-website-${source}`}>Website</label>
          <input
            id={`nl-website-${source}`}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={e => setWebsite(e.target.value)}
          />
        </div>

        <label className="newsletter-consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
          />
          <span className="text-small">
            Yes, email me the weekly newsletter. I can unsubscribe from any email, and I've read
            the <Link to="/privacy">privacy policy</Link>.
          </span>
        </label>

        <button className="btn-sage newsletter-submit" type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Signing you up…' : 'Send me the newsletter'}
        </button>

        {status === 'error' && (
          <p className="newsletter-error text-small" role="alert">{message}</p>
        )}
      </form>
    </div>
  );
}
